import orderBy from 'lodash.orderby';

import { nameResolver } from '@/_shared';

import { getParentAgreementLink } from './get-parent-agreement-link';
import type { InvoiceQuerySchema } from './schemas/invoices/invoice-query-schema';

export type InvoiceEmailRecipientRole = 'billing' | 'notification';

export const invoiceEmailRecipientRoleLabel: Record<InvoiceEmailRecipientRole, string> = {
  billing: 'Billing Contact',
  notification: "Add'l Billing",
};

export type InvoiceEmailRecipient = {
  id: number;
  name: string;
  email: string | null;
  role: InvoiceEmailRecipientRole;
  /** Contact profile image when resolved from agreement/invoice contacts */
  avatarUrl?: string | null;
};

type ContactLike = {
  id?: number | null;
  first_name?: string | null;
  last_name?: string | null;
  emails?: { email?: string | null; pivot?: { default?: number } | null }[] | null;
  profile_image?: { url?: string | null }[] | null;
};

function getContactAvatarUrl(contact: ContactLike): string | null {
  const url = contact.profile_image?.at(0)?.url?.trim();
  return url || null;
}

/** One email per contact — default first, matching agreement billing contact display order. */
export function getContactPrimaryEmail(contact: ContactLike): string | null {
  const emails = contact.emails ?? [];
  if (emails.length === 0) {
    return null;
  }
  const [primary] = orderBy(emails, [(e) => e.pivot?.default ?? 0], ['desc']);
  const value = primary?.email?.trim();
  return value || null;
}

function toRecipient(contact: ContactLike, role: InvoiceEmailRecipientRole): InvoiceEmailRecipient | null {
  if (typeof contact.id !== 'number' || contact.id <= 0) {
    return null;
  }
  return {
    id: contact.id,
    name: nameResolver(contact.first_name, contact.last_name, 'Contact'),
    email: getContactPrimaryEmail(contact),
    role,
    avatarUrl: getContactAvatarUrl(contact),
  };
}

function addRecipients(
  map: Map<number, InvoiceEmailRecipient>,
  contacts: ContactLike[] | ContactLike | null | undefined,
  role: InvoiceEmailRecipientRole
) {
  const list = contacts == null ? [] : Array.isArray(contacts) ? contacts : [contacts];
  for (const contact of list) {
    const recipient = toRecipient(contact, role);
    if (!recipient || map.has(recipient.id)) {
      continue;
    }
    map.set(recipient.id, recipient);
  }
}

/** Billing contacts first, then additional billing notification contacts. */
export function orderInvoiceEmailRecipients(recipients: InvoiceEmailRecipient[]): InvoiceEmailRecipient[] {
  const billing = recipients.filter((r) => r.role === 'billing');
  const notification = recipients.filter((r) => r.role === 'notification');
  return [...billing, ...notification];
}

/** Invoice billing recipients — billing first, then add'l billing. Falls back to parent agreement when invoice has none. */
export function getInvoiceEmailRecipients(invoice: InvoiceQuerySchema): InvoiceEmailRecipient[] {
  const map = new Map<number, InvoiceEmailRecipient>();

  if (invoice.billing_contact) {
    addRecipients(map, invoice.billing_contact, 'billing');
  }
  addRecipients(map, invoice.billing_email_contacts, 'notification');

  if (map.size === 0) {
    const parentAgreement = getParentAgreementLink(invoice.agreement_links)?.agreement;
    addRecipients(map, parentAgreement?.billing_contact, 'billing');
    addRecipients(map, parentAgreement?.billing_email_contacts, 'notification');
  }

  return orderInvoiceEmailRecipients([...map.values()]);
}

export function getInvoiceEmailContactIds(invoice: InvoiceQuerySchema): number[] {
  return getInvoiceEmailRecipients(invoice).map((r) => r.id);
}

/** Primary recipient email strings (billing + notification), same order as send/copy recipients. */
export function getInvoiceRecipientEmailAddresses(invoice: InvoiceQuerySchema): string[] {
  return getInvoiceEmailRecipients(invoice)
    .map((r) => r.email?.trim())
    .filter((email): email is string => Boolean(email));
}
